import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { getRestoredCartItems } from '@/lib/pos/order-change'
import { cartMutationKeys, cartQueryKeys } from '@/lib/services/carts/carts.query'
import type { OrderChangeRow } from '@/lib/services/carts/carts.types'
import { nextTempLineId, queueCartWrite, writingCart } from '@/lib/services/carts/cart-writer'
import appQueryClient from '@/lib/queryClient'
import { cashDrawerQueryKeys } from '@/lib/services/cash-drawers/cash-drawers.query'
import { reportQueryKeys } from '@/lib/services/reports/reports.query'
import { useMutation } from '@tanstack/react-query'
import {
  applySalesOrderChange,
  openSalesOrderEditCart,
  restoreSalesOrderChangeItem,
  type OpenSalesOrderEditCartInput,
  type RestoreSalesOrderChangeItemInput,
} from './sales-order-changes.api'
import type { ApplySalesOrderChangeInput } from './sales-order-changes.types'
import { invalidateOrderUpdateQueries } from './sales-orders.query'

const salesOrderChangeMutationKeys = {
  openEditCart: ['sales-order', 'openEditCart'],
  applyChange: ['sales-order', 'applyChange'],
}

/** Opens (or resumes) the POS draft of a change to an issued order. */
export function useOpenSalesOrderEditCartMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: salesOrderChangeMutationKeys.openEditCart,
    mutationFn: (values: OpenSalesOrderEditCartInput) =>
      openSalesOrderEditCart({ organizationId, ...values }),
  })
}

interface RestartSalesOrderChangeVariables {
  salesOrderId: number
  /** The draft to discard; a repeat returns the draft that replaced it. */
  cartId: number
}

/**
 * Discards the draft and starts the change again from the order as it is. It waits on the draft's line behind its
 * edits, and counts as the draft ending, so no edit is sent to the draft it discards.
 */
export function useRestartSalesOrderChangeMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ salesOrderId, cartId }: RestartSalesOrderChangeVariables) =>
      queueCartWrite(organizationId, cartId, {
        mutationKey: cartMutationKeys.delete,
        send: () =>
          writingCart(organizationId, cartId, () =>
            openSalesOrderEditCart({ organizationId, salesOrderId, restartCartId: cartId })
          ),
      }),
  })
}

interface RestoreSalesOrderChangeItemVariables extends RestoreSalesOrderChangeItemInput {
  /** The removed rows the restore puts back, shown until the server answers. */
  preview: OrderChangeRow[]
}

/**
 * Restores a removed item to the draft as a cart edit: it queues on the cart's line behind earlier edits, shows the
 * item back in the cart meanwhile, and a read the restore overlaps can't cross it out again.
 */
export function useRestoreSalesOrderChangeItem() {
  const organizationId = useRouteOrganizationId()

  return ({
    salesOrderId,
    cartId,
    salesOrderItemId,
    preview,
  }: RestoreSalesOrderChangeItemVariables) =>
    queueCartWrite(organizationId, cartId, {
      // Counted with the cart's own edits, so the screen waits on it as it does on adding an item.
      mutationKey: cartMutationKeys.addItem,
      // A repeated click finds the item already back, as the server does.
      show: (cart) => {
        const inCart = new Set(cart.cart_items.map((item) => item.source_sales_order_item_id))
        return {
          ...cart,
          cart_items: [
            ...cart.cart_items,
            ...getRestoredCartItems(preview, cartId, nextTempLineId).filter(
              (item) => !inCart.has(item.source_sales_order_item_id)
            ),
          ],
        }
      },
      send: async () => {
        const cart = await writingCart(organizationId, cartId, () =>
          restoreSalesOrderChangeItem({ organizationId, salesOrderId, cartId, salesOrderItemId })
        )
        appQueryClient.setQueryData(cartQueryKeys.posShow({ organizationId, cartId }), cart)
        return cart
      },
    })
}

/**
 * Saves an edit cart as a change to its order without collecting anything; the payment a lower total no longer
 * needs goes back with the save, and anything else moves through the order's own flows. The save waits on the
 * draft's line behind its edits, and counts as the order being finished, so no edit is sent to the draft it closes.
 * The caller leaves the draft, then refreshes with `refreshSavedOrderChange`.
 */
export function useApplySalesOrderChangeMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: salesOrderChangeMutationKeys.applyChange,
    mutationFn: (values: ApplySalesOrderChangeInput) =>
      queueCartWrite(organizationId, values.cart_id, {
        mutationKey: cartMutationKeys.finalizeSalesOrder,
        send: () =>
          writingCart(organizationId, values.cart_id, () =>
            applySalesOrderChange({ organizationId, ...values })
          ),
      }),
  })
}

/**
 * What reads again once a change to an order saves, by its own save or by the Pay Now payment that saved it. Call it
 * after leaving the edit screen: the draft closed with the save, so it is only marked stale, never read again while
 * the screen may still show it (it would show the change closed, or remove it and wait on a read).
 */
export function refreshSavedOrderChange(organizationId: number, cartId: number) {
  return Promise.all([
    // The order, its history, and the customer's balance the change moved.
    invalidateOrderUpdateQueries(organizationId, true),
    // A change that hands cash back moves a till's totals, as a refund does.
    appQueryClient.invalidateQueries({ queryKey: cashDrawerQueryKeys.all({ organizationId }) }),
    appQueryClient.invalidateQueries({ queryKey: reportQueryKeys.cashDrawer({ organizationId }) }),
    appQueryClient.invalidateQueries({
      queryKey: cartQueryKeys.posShow({ organizationId, cartId }),
      refetchType: 'none',
    }),
  ])
}
